/**
 * ExportRecordScope — record selection step for export.
 *
 * Surfaces the three canonical scopes (all authorized records, current
 * filtered results, selected rows) as described in plan section 28.
 */
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../i18n/resolve-key';

export type ExportScope = 'all' | 'filtered' | 'selected';

export interface ExportRecordScopeProps {
  value: ExportScope;
  onChange: (scope: ExportScope) => void;
  totalCount?: number;
  filteredCount?: number;
  selectedCount?: number;
  availableScopes?: ExportScope[];
}

const DEFAULT_SCOPES: ExportScope[] = ['all', 'filtered', 'selected'];

export function ExportRecordScope({
  value,
  onChange,
  totalCount,
  filteredCount,
  selectedCount,
  availableScopes = DEFAULT_SCOPES,
}: ExportRecordScopeProps) {
  const { t } = useTranslation();

  const scopeLabel = (scope: ExportScope): string => {
    switch (scope) {
      case 'all':
        return totalCount !== undefined
          ? t(resolveTKey('tableExport.scope.allCount'), { count: totalCount })
          : t(resolveTKey('tableExport.scope.all'));
      case 'filtered':
        return filteredCount !== undefined
          ? t(resolveTKey('tableExport.scope.filteredCount'), { count: filteredCount })
          : t(resolveTKey('tableExport.scope.filtered'));
      case 'selected':
        return selectedCount !== undefined
          ? t(resolveTKey('tableExport.scope.selectedCount'), { count: selectedCount })
          : t(resolveTKey('tableExport.scope.selected'));
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t(resolveTKey('tableExport.records.title'))}
      </Typography>
      <RadioGroup
        value={value}
        onChange={(_, v) => onChange(v as ExportScope)}
        aria-label={t(resolveTKey('tableExport.records.title'))}
      >
        {availableScopes.map((scope) => (
          <FormControlLabel
            key={scope}
            value={scope}
            control={<Radio size="small" />}
            label={scopeLabel(scope)}
            disabled={scope === 'selected' && (selectedCount ?? 0) === 0}
          />
        ))}
      </RadioGroup>
    </Box>
  );
}
